"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InternalClientChannel = void 0;
const ChainBuilder_1 = require("../common/ChainBuilder");
const MessageDefinition_1 = __importDefault(require("../common/MessageDefinition"));
const TransmissionTypes_1 = require("../common/TransmissionTypes");
const ClientChain_1 = require("./ClientChain");
const ClientMessage_1 = require("./ClientMessage");
class InternalClientChannel {
    /**
     * @param namespace The channel namespace that this channel's ID exists within.
     * @param channelID The ID of this channel.
     * @param client The MessengerClient that will be used for sending messages to the server.
     */
    constructor(namespace, channelID, client) {
        this.messages = {};
        this.chains = {};
        /**
         * Delivers the message, which was received from the server, to the correct application-level
         * handling code.
         *
         * @param message The message to deliver.
         */
        this.deliverMessageToApplication = (message) => {
            if (message.chain) {
                const chain = this.chains[message.chainID];
                if (chain === undefined) {
                    console.warn("Unregistered chain message received. Will be dropped:", message);
                    return;
                }
                chain.dispatch(message);
            }
            else {
                if (this.messages[message.messageID] === undefined) {
                    console.warn("Unregistered message received. Will be dropped:", message);
                    return;
                }
                this.messages[message.messageID].forEach((serverMessage) => serverMessage.dispatch(message));
            }
        };
        /**
         * Registers a new MessageDefinition, which is necessary to establish handlers for the message
         * and send messages of this type to the server.
         *
         * @param message A MessageDefinition describing the structure of this message.
         * @return A ChannelMessage that can be used to establish handlers and send messages of this type.
         */
        this.registerMessage = (message) => {
            // TODO - Allowing multiple registrations of a message violates type safety, since there's technically nothing
            //        requiring that the same ID is associated with the same payload.
            //        However, this is the same violation of type safety involved between the client and the server, since
            //        they could potentially register different MessageDefinitions (payload shapes) for the same ID.
            //        We'll never get a missing property because of this, because the runtime typechecking from io-ts is
            //        downstream of this.
            if (this.messages[message.id] === undefined) {
                this.messages[message.id] = [];
            }
            const channelMessage = new ClientMessage_1.InternalClientMessage(message, this);
            this.messages[message.id].push(channelMessage);
            return channelMessage;
        };
        this.registerGroup = (messages) => {
            const result = {};
            Object.keys(messages).forEach((key) => {
                const def = messages[key];
                if (def instanceof MessageDefinition_1.default) {
                    result[key] = this.registerMessage(def);
                }
                else {
                    result[key] = this.registerChain(def);
                }
            });
            return result;
        };
        this.registerChain = (chain) => {
            // TODO - A chain may only be registered once, since we need to enforce within the ClientChain that there is only
            //        one handler, we also need to enforce that there's only one ClientChain.
            //        For now, error out if the chain is registered more than once. In the future, we can consider returning
            //        the already registered chain if we find a use-case.
            if (this.chains[chain.id] !== undefined) {
                throw Error("Cannot register the same chain more than once.");
            }
            if (chain.direction === ChainBuilder_1.ChainDirection.ClientFirst) {
                const clientChain = new ClientChain_1.InternalCFClientChain(chain, this);
                this.chains[chain.id] = clientChain;
                return clientChain; // Unfortunate but necessary...
            }
            else {
                const clientChain = new ClientChain_1.InternalSFClientChain(chain, this);
                this.chains[chain.id] = clientChain;
                return clientChain;
            }
        };
        /**
         * Sends the provided message and payload to the server.
         *
         * @param messageID The ID of this message.
         * @param payload The payload of the message.
         */
        this.sendMessage = (messageID, payload) => {
            const message = TransmissionTypes_1.buildRegularMessage(this.namespace, this.channelID, messageID, payload);
            this.client.sendMessage(message);
        };
        /**
         * Sends the provided chain message to the server
         *
         * @param chainID The ID of the chain this message is a part of.
         * @param chainKey The identifier of the chain instance we are sending.
         * @param chainIndex The index of the message in the chain being sent.
         * @param payload The payload of the message to send.
         */
        this.sendChain = (chainID, chainKey, chainIndex, payload) => {
            const chainMessage = TransmissionTypes_1.buildChainMessage(chainKey, chainIndex, this.namespace, this.channelID, chainID, payload);
            this.client.sendMessage(chainMessage);
        };
        /**
         * Drops the requested chain, indicating that no more messages will be sent for this chain.
         *
         * @param chainID The ID of the chain.
         * @param chainKey The chain identifier to drop.
         */
        this.dropChain = (chainID, chainKey) => {
            const clientChain = this.chains[chainID];
            if (clientChain === undefined) {
                console.warn("Got DropChainMessage for a nonexistent chain. Ignoring.");
                return;
            }
            clientChain.handleDropMessage(chainKey);
        };
        /**
         * Returns a public-facing handle to this object with properties that should not be accessible to
         * application code removed.
         */
        this.publicHandle = () => {
            return this;
        };
        this.namespace = namespace;
        this.channelID = channelID;
        this.client = client;
    }
}
exports.InternalClientChannel = InternalClientChannel;
//# sourceMappingURL=ClientChannel.js.map